/* eslint-disable max-lines -- Figma-exported illustration; the length is SVG path/filter data, not logic */
import { FormattedMessage, useIntl } from 'react-intl';

import type { SVGProps } from 'react';

/**
 * Illustration for the Evidence downgrade-reconsider modal: an "Evidence"
 * document card listing 14 sources, with "Local guidelines" / "Primary
 * research" source tags and a PDF card. Converted from the Figma export
 * (downgrade-source-control.svg) so the labels can be translated via
 * react-intl.
 */
// theme-agnostic: fixed-palette Figma illustration, renders identically in both themes
export const DowngradeSourceControlSvg = (props: SVGProps<SVGSVGElement>) => {
  const intl = useIntl();

  return (
    // direction="ltr": the artwork is composed left-to-right; without it, an
    // inherited dir="rtl" (Arabic) flips text-anchor's start side and every
    // label extends leftward from its authored x, clipping out of the canvas.
    <svg
      viewBox="0 0 613 415"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      direction="ltr"
      role="img"
      aria-label={intl.formatMessage({
        defaultMessage:
          'Illustration of an Evidence document listing 14 sources, with Local guidelines, Primary research and PDF source cards',
        id: 'c/ez8t81eF',
      })}
      {...props}
    >
      <g clipPath="url(#downgrade-source-control-clip0)">
        <rect width="613" height="415" fill="#FCFAF8" />
        <g filter="url(#downgrade-source-control-filter0)">
          <rect
            x="85"
            y="146"
            width="439"
            height="235"
            rx="7.95387"
            fill="white"
            shapeRendering="crispEdges"
          />
          <text
            fill="#211217"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-exposure"
            fontSize="40"
            letterSpacing="-0.025em"
          >
            <tspan x="101.271" y="189.906">
              <FormattedMessage
                defaultMessage="Evidence"
                id="BZwtRymnbA"
                description="Title of a mock document in the source-control illustration on the downgrade modal — the name of Heidi's Evidence feature, shown above a '14 sources' count. Limited space — keep the translation as short as the English or it will overflow"
              />
            </tspan>
          </text>
          <text
            fill="#211217"
            style={{ whiteSpace: 'pre' }}
            xmlSpace="preserve"
            className="font-exposure"
            fontSize="24"
            letterSpacing="-0.025em"
          >
            <tspan x="100.995" y="222.614">
              <FormattedMessage
                defaultMessage="14 sources"
                id="wUlkMWxVZc"
                description="Source count under the 'Evidence' title in the source-control illustration on the downgrade modal — how many reference sources the mock document cites. Limited space — keep the translation as short as the English or it will overflow"
              />
            </tspan>
          </text>
          <rect
            x="100.908"
            y="238.723"
            width="278"
            height="16"
            rx="3.97694"
            fill="#D4C4C9"
          />
          <rect
            x="100.908"
            y="262.677"
            width="343"
            height="16"
            rx="3.97694"
            fill="#D4C4C9"
          />
          <rect
            x="100.908"
            y="286.631"
            width="235"
            height="16"
            rx="3.97694"
            fill="#D4C4C9"
          />
          <rect
            x="100.908"
            y="310.585"
            width="318"
            height="16"
            rx="3.97694"
            fill="#D4C4C9"
          />
          <g clipPath="url(#downgrade-source-control-clip1)">
            <g filter="url(#downgrade-source-control-filter1)">
              <path
                d="M100.908 351.173C100.908 348.977 102.689 347.196 104.885 347.196H114.827C117.024 347.196 118.804 348.977 118.804 351.173V361.116C118.804 363.312 117.024 365.093 114.827 365.093H104.885C102.689 365.093 100.908 363.312 100.908 361.116V351.173Z"
                fill="white"
              />
              <path
                d="M104.885 347.445H114.827C116.886 347.445 118.555 349.114 118.556 351.173V361.115C118.556 363.174 116.886 364.844 114.827 364.844H104.885C102.826 364.844 101.157 363.174 101.157 361.115V351.173C101.157 349.114 102.826 347.446 104.885 347.445Z"
                stroke="#755760"
                strokeWidth="0.497117"
              />
              <g clipPath="url(#downgrade-source-control-clip2)">
                <g clipPath="url(#downgrade-source-control-clip3)">
                  <path
                    d="M112.985 355.829L109.939 358.875C109.566 359.248 109.06 359.458 108.533 359.458C108.005 359.458 107.499 359.248 107.126 358.875C106.753 358.502 106.543 357.996 106.543 357.468C106.543 356.941 106.753 356.435 107.126 356.061L109.966 353.221C110.215 352.972 110.552 352.832 110.904 352.832C111.256 352.831 111.594 352.971 111.843 353.22C112.093 353.468 112.233 353.806 112.233 354.158C112.233 354.51 112.094 354.848 111.845 355.097L108.998 357.937C108.874 358.062 108.705 358.131 108.529 358.131C108.353 358.131 108.185 358.062 108.06 357.937C107.936 357.813 107.866 357.644 107.866 357.468C107.866 357.292 107.936 357.124 108.06 356.999L110.874 354.189"
                    stroke="#755760"
                    strokeWidth="0.59654"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </g>
            </g>
            <path
              d="M125.764 347.445H204.806C206.865 347.445 208.534 349.114 208.534 351.173V361.115C208.534 363.174 206.865 364.844 204.806 364.844H125.764C123.705 364.844 122.036 363.174 122.036 361.115V351.173C122.036 349.114 123.705 347.446 125.764 347.445Z"
              fill="white"
            />
            <path
              d="M125.764 347.445H204.806C206.865 347.445 208.534 349.114 208.534 351.173V361.115C208.534 363.174 206.865 364.844 204.806 364.844H125.764C123.705 364.844 122.036 363.174 122.036 361.115V351.173C122.036 349.114 123.705 347.446 125.764 347.445Z"
              stroke="#E9E2E3"
              strokeWidth="0.497117"
            />
            <g clipPath="url(#downgrade-source-control-clip4)">
              <g clipPath="url(#downgrade-source-control-clip5)">
                <path
                  d="M130.404 352.83V354.156C130.404 354.332 130.474 354.5 130.598 354.624C130.722 354.749 130.891 354.819 131.067 354.819H132.392M130.735 352.83H127.753C127.577 352.83 127.408 352.9 127.284 353.024C127.16 353.149 127.09 353.317 127.09 353.493V358.795C127.09 358.971 127.16 359.14 127.284 359.264C127.408 359.388 127.577 359.458 127.753 359.458H131.73C131.905 359.458 132.074 359.388 132.198 359.264C132.323 359.14 132.392 358.971 132.392 358.795V354.487L130.735 352.83Z"
                  stroke="#755760"
                  strokeWidth="0.59654"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </g>
            <rect
              x="136.701"
              y="352.167"
              width="68.105"
              height="7.95387"
              rx="1.98847"
              fill="#E9E2E3"
            />
            <rect
              x="316.18"
              y="350.258"
              width="69.0993"
              height="11.9308"
              rx="1.98847"
              fill="#E9E2E3"
            />
          </g>
        </g>
        <g filter="url(#downgrade-source-control-filter2)">
          <path
            d="M499 188.908C499 185.166 502.034 182.132 505.776 182.132H574.94C578.683 182.132 581.716 185.166 581.716 188.908V263.98C581.716 267.723 578.683 270.756 574.94 270.756H505.776C502.034 270.756 499 267.723 499 263.98V188.908Z"
            fill="white"
          />
        </g>
        <text
          fill="#28030F"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-body"
          fontSize="18.9598"
          fontWeight="500"
          letterSpacing="-0.02em"
        >
          <tspan x="512.393" y="259.321">
            <FormattedMessage
              defaultMessage="PDF"
              id="+Q3x/Gj9L3"
              description="File-format label on a mock document card in the source-control illustration on the downgrade modal — the PDF file format acronym, usually kept as 'PDF'. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
        <path
          d="M512.885 200.448H567.832"
          stroke="#D4C4C9"
          strokeWidth="3.30865"
          strokeLinecap="round"
        />
        <path
          d="M512.885 214.037H567.832"
          stroke="#D4C4C9"
          strokeWidth="3.30865"
          strokeLinecap="round"
        />
        <path
          d="M512.885 226.444H567.832"
          stroke="#D4C4C9"
          strokeWidth="3.30865"
          strokeLinecap="round"
        />
        <rect
          x="326"
          y="58"
          width="235.162"
          height="48.9327"
          rx="9.69954"
          fill="#E9E2E3"
        />
        <g clipPath="url(#downgrade-source-control-clip6)">
          <g clipPath="url(#downgrade-source-control-clip7)">
            <path
              d="M347.601 76.2754V78.7519C347.601 79.0803 347.732 79.3952 347.964 79.6274C348.196 79.8597 348.511 79.9901 348.84 79.9901H351.316M348.22 76.2754H342.648C342.32 76.2754 342.005 76.4058 341.773 76.6381C341.541 76.8703 341.41 77.1852 341.41 77.5136V87.4195C341.41 87.7479 341.541 88.0629 341.773 88.2951C342.005 88.5273 342.32 88.6578 342.648 88.6578H350.078C350.406 88.6578 350.721 88.5273 350.953 88.2951C351.186 88.0629 351.316 87.7479 351.316 87.4195V79.371L348.22 76.2754Z"
              stroke="#28030F"
              strokeWidth="1.07314"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </g>
        <text
          fill="#28030F"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-body"
          fontSize="24"
          fontWeight="500"
          letterSpacing="-0.02em"
        >
          <tspan x="359.723" y="91.1936">
            <FormattedMessage
              defaultMessage="Primary research"
              id="STlJFgd9Y/"
              description="Label on a source-type tag with a document icon, next to the 'Local guidelines' tag, in the source-control illustration on the downgrade modal — a category of reference sources. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
        <rect
          x="30"
          y="42"
          width="224.162"
          height="48.9327"
          rx="9.69954"
          fill="#E9E2E3"
        />
        <g clipPath="url(#downgrade-source-control-clip8)">
          <g clipPath="url(#downgrade-source-control-clip9)">
            <path
              d="M51.6014 60.2754V62.7519C51.6014 63.0803 51.7318 63.3952 51.964 63.6274C52.1962 63.8597 52.5112 63.9901 52.8396 63.9901H55.3161M52.2205 60.2754H46.6484C46.32 60.2754 46.005 60.4058 45.7728 60.6381C45.5406 60.8703 45.4102 61.1852 45.4102 61.5136V71.4195C45.4102 71.7479 45.5406 72.0629 45.7728 72.2951C46.005 72.5273 46.32 72.6578 46.6484 72.6578H54.0778C54.4062 72.6578 54.7212 72.5273 54.9534 72.2951C55.1856 72.0629 55.3161 71.7479 55.3161 71.4195V63.371L52.2205 60.2754Z"
              stroke="#28030F"
              strokeWidth="1.07314"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </g>
        <text
          fill="#28030F"
          style={{ whiteSpace: 'pre' }}
          xmlSpace="preserve"
          className="font-body"
          fontSize="24"
          fontWeight="500"
          letterSpacing="-0.02em"
        >
          <tspan x="63.309" y="75.1936">
            <FormattedMessage
              defaultMessage="Local guidelines"
              id="SWQ+n6vw39"
              description="Label on a source-type tag with a document icon, next to the 'Primary research' tag, in the source-control illustration on the downgrade modal — a category of reference sources. Limited space — keep the translation as short as the English or it will overflow"
            />
          </tspan>
        </text>
      </g>
      <defs>
        <filter
          id="downgrade-source-control-filter0"
          x="69.0923"
          y="136.058"
          width="470.815"
          height="266.815"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="5.9654" />
          <feGaussianBlur stdDeviation="7.95387" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.1 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="downgrade-source-control-filter1"
          x="99.914"
          y="346.699"
          width="19.885"
          height="19.8845"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="0.497117" />
          <feGaussianBlur stdDeviation="0.497117" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <filter
          id="downgrade-source-control-filter2"
          x="483.508"
          y="168.667"
          width="113.701"
          height="119.608"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="2.02695" />
          <feGaussianBlur stdDeviation="7.74597" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.156863 0 0 0 0 0.0117647 0 0 0 0 0.0588235 0 0 0 0.15 0"
          />
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
        </filter>
        <clipPath id="downgrade-source-control-clip0">
          <rect width="613" height="415" fill="white" />
        </clipPath>
        <clipPath id="downgrade-source-control-clip1">
          <rect
            width="407.185"
            height="17.8962"
            fill="white"
            transform="translate(100.908 347.196)"
          />
        </clipPath>
        <clipPath id="downgrade-source-control-clip2">
          <rect
            width="7.95387"
            height="7.95387"
            fill="white"
            transform="translate(105.879 352.167)"
          />
        </clipPath>
        <clipPath id="downgrade-source-control-clip3">
          <rect
            width="7.95387"
            height="7.95387"
            fill="white"
            transform="translate(105.879 352.167)"
          />
        </clipPath>
        <clipPath id="downgrade-source-control-clip4">
          <rect
            width="7.95387"
            height="7.95387"
            fill="white"
            transform="translate(125.764 352.167)"
          />
        </clipPath>
        <clipPath id="downgrade-source-control-clip5">
          <rect
            width="7.95387"
            height="7.95387"
            fill="white"
            transform="translate(125.764 352.167)"
          />
        </clipPath>
        <clipPath id="downgrade-source-control-clip6">
          <rect x="338.934" y="75.0371" width="14.8589" height="14.8589" fill="white" />
        </clipPath>
        <clipPath id="downgrade-source-control-clip7">
          <rect
            width="14.8589"
            height="14.8589"
            fill="white"
            transform="translate(338.934 75.0371)"
          />
        </clipPath>
        <clipPath id="downgrade-source-control-clip8">
          <rect x="42.9336" y="59.0371" width="14.8589" height="14.8589" fill="white" />
        </clipPath>
        <clipPath id="downgrade-source-control-clip9">
          <rect
            width="14.8589"
            height="14.8589"
            fill="white"
            transform="translate(42.9336 59.0371)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
